import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {
  ApiCollection,
  ApiResponse,
  EmployeeSurveyAssignmentRow,
  EmployeeSurveyAssignmentDetail,
  SurveyDetail,
  SurveyQuestionGroup,
  SurveyQuestion,
  SurveyRow
} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';

@Injectable({
  providedIn: 'root'
})
export class SurveyService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getSurveys(page = 1, itemsPerPage = 50, options?: {skipLoader?: boolean}): Promise<ApiResponse<SurveyRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEYS, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const surveys = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapSurvey(item));
      return {success: true, result: surveys};
    } catch (error: any) {
      log.error('Error loading surveys:', error);
    }
    return {success: false, message: 'No se pudieron cargar las encuestas.'};
  }

  async getSurvey(surveyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<SurveyDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_DETAIL, {id: surveyId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapSurveyDetail(response)};
    } catch (error: any) {
      log.error('Error loading survey detail:', error);
    }
    return {success: false, message: 'No se pudo cargar la encuesta.'};
  }

  async createSurvey(
    payload: {name?: string; title?: string; description?: string; dueDate?: string | null; status?: string},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<SurveyDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEYS);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, this.toSurveyWritePayload(payload), headers ? {headers} : undefined);
      return {success: true, result: this.mapSurveyDetail(response)};
    } catch (error: any) {
      log.error('Error creating survey:', error);
    }
    return {success: false, message: 'No se pudo crear la encuesta.'};
  }

  async updateSurvey(
    surveyId: string | number,
    payload: {name?: string; title?: string; description?: string; dueDate?: string | null; status?: string},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<SurveyDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_DETAIL, {id: surveyId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(url, this.toSurveyWritePayload(payload), {headers, json: false});
      return {success: true, result: this.mapSurveyDetail(response)};
    } catch (error: any) {
      log.error('Error updating survey:', error);
      if (String(error?.code || '') === '422') {
        return {
          success: false,
          message: this.extractValidationMessage(error) || 'No se pudo actualizar la encuesta por validaciones.'
        };
      }
    }
    return {success: false, message: 'No se pudo actualizar la encuesta.'};
  }

  async saveQuestions(surveyId: string | number, questions: SurveyQuestion[], questionBlocks: SurveyQuestionGroup[] = []): Promise<ApiResponse<SurveyDetail | undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_QUESTIONS, {id: surveyId});
      const headers = this.buildHeaders();
      const sanitizedQuestions = questions
        .map((question, index) => {
          const apiType = this.toApiQuestionType(question.type);
          return {
            id: this.toPersistedQuestionId(question.id),
            block: (question.block || '').trim() || null,
            text: (question.text || '').trim(),
            description: (question.description || '').trim() || null,
            type: apiType,
            options: this.toApiQuestionOptions(question),
            order: Number(question.priority || (index + 1)),
            questionVariables: this.isVariableAssociationEligibleType(apiType)
              ? this.toApiQuestionVariables(question)
              : []
          };
        })
        .filter(question => Boolean(question.text));
      const payload = {
        questionBlocks: questionBlocks
          .map(block => ({
            id: this.toPersistedQuestionId(block.id),
            name: (block.name || '').trim(),
            description: (block.description || '').trim() || null,
          }))
          .filter(block => Boolean(block.name)),
        questions: sanitizedQuestions
      };
      const response: any = await this.apiService.post(url, payload, headers ? {headers} : undefined);
      return {success: true, result: this.mapSurveyDetail(response)};
    } catch (error: any) {
      log.error('Error saving survey questions:', error);
    }
    return {success: false, message: 'No se pudieron guardar las preguntas.'};
  }

  async assignSurvey(
    surveyId: string | number,
    payload: {
      employeeIds?: Array<string | number>;
      groupIds?: Array<string | number>;
      forceNewDispatch?: boolean;
      dispatchKey?: string | null;
    },
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_ASSIGNMENTS, {id: surveyId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.post(url, payload, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error assigning survey:', error);
    }
    return {success: false, message: 'No se pudo asociar la encuesta.'};
  }

  async unassignSurvey(surveyId: string | number, assignmentId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_ASSIGNMENT_DETAIL, {id: surveyId, assignmentId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error unassigning survey:', error);
    }
    return {success: false, message: 'No se pudo quitar la asignación.'};
  }

  async deleteSurvey(surveyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_DETAIL, {id: surveyId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting survey:', error);
    }
    return {success: false, message: 'No se pudo eliminar la encuesta.'};
  }

  async getResults(surveyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<any>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_RESULTS, {id: surveyId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error loading survey results:', error);
    }
    return {success: false, message: 'No se pudieron cargar los resultados.'};
  }

  async getProgress(surveyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<any>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_PROGRESS, {id: surveyId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error loading survey progress:', error);
    }
    return {success: false, message: 'No se pudo cargar el progreso.'};
  }

  async getInsights(surveyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<any>> {
    try {
      const url = GET_URL(ENDPOINTS.SURVEY_INSIGHTS, {id: surveyId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error loading survey insights:', error);
    }
    return {success: false, message: 'No se pudieron cargar los insights.'};
  }

  async getEmployeeSurveyAssignment(
    assignmentId: string | number,
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<EmployeeSurveyAssignmentDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_SURVEY_ASSIGNMENT_DETAIL, {assignmentId});
      let headers = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      headers = headers.set('X-Skip-Error', '1');
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapEmployeeSurveyAssignmentDetail(response)};
    } catch (error: any) {
      log.error('Error loading employee survey assignment detail:', error);
      return {
        success: false,
        message: this.extractValidationMessage(error) || 'No se pudo cargar la encuesta asignada.'
      };
    }
  }

  async getEmployeeSurveyAssignments(
    status?: 'pending' | 'completed',
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<EmployeeSurveyAssignmentRow[]> & {totalItems?: number}> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_SURVEY_ASSIGNMENTS, status ? {status} : undefined);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const normalized = this.apiService.normalizeCollection<any>(response);
      const items = normalized.member.map((item: any) => ({
        assignmentId: item.assignmentId ?? item.id,
        status: item.status,
        answeredAt: item.answeredAt ?? null,
        surveyId: item.surveyId,
        surveyTitle: item.surveyTitle,
        surveyStatus: item.surveyStatus,
        questionsCount: item.questionsCount ?? 0,
        companyId: item.companyId,
        companyName: item.companyName,
      }));
      return {success: true, result: items, totalItems: normalized.totalItems};
    } catch (error: any) {
      log.error('Error loading employee survey assignments:', error);
    }

    return {success: false, message: 'No se pudieron cargar tus encuestas.', totalItems: 0};
  }

  async submitEmployeeSurveyAssignmentResponses(
    assignmentId: string | number,
    answers: Array<{questionId: string | number; value: string | number | string[]}>
  ): Promise<ApiResponse<EmployeeSurveyAssignmentDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_SURVEY_ASSIGNMENT_RESPONSES, {assignmentId});
      let headers = this.buildHeaders() ?? new HttpHeaders();
      headers = headers.set('X-Skip-Error', '1');
      const response: any = await this.apiService.post(url, {answers}, headers ? {headers} : undefined);
      return {success: true, result: this.mapEmployeeSurveyAssignmentDetail(response)};
    } catch (error: any) {
      log.error('Error submitting employee survey responses:', error);
      return {
        success: false,
        message: this.extractValidationMessage(error) || 'No se pudo enviar la respuesta de la encuesta.'
      };
    }
  }

  private mapSurvey(item: any): SurveyRow {
    return {
      id: item.id,
      name: item.name || item.title || 'Encuesta',
      description: item.description || item.summary || item.details || '',
      status: this.normalizeSurveyStatus(item.status ?? item.state),
      dueDate: item.dueDate ?? item.due_date ?? null,
      questionsCount: item.questionsCount ?? item.totalQuestions ?? item.questions ?? 0,
      employeesCount: item.employeesCount ?? item.employees ?? item.totalEmployees ?? 0,
      completedCount: item.completedCount ?? item.completed ?? item.responses ?? 0,
      questions: Array.isArray(item.questions)
        ? item.questions.map((question: any) => this.mapQuestion(question))
        : [],
      questionBlocks: Array.isArray(item.questionBlocks)
        ? item.questionBlocks.map((block: any) => this.mapQuestionBlock(block))
        : [],
      assignments: this.mapSurveyAssignments(item.assignments)
    };
  }

  private toSurveyWritePayload(payload: {name?: string; title?: string; description?: string; dueDate?: string | null; status?: string}): {title?: string; description?: string; dueDate?: string | null; status?: string} {
    const body: {title?: string; description?: string; dueDate?: string | null; status?: string} = {};

    const title = payload.title ?? payload.name;
    if (typeof title === 'string') {
      body.title = title;
    }

    if (typeof payload.description === 'string') {
      body.description = payload.description;
    }

    if (payload.dueDate !== undefined) {
      body.dueDate = payload.dueDate;
    }

    if (typeof payload.status === 'string') {
      body.status = payload.status;
    }

    return body;
  }

  private extractValidationMessage(error: any): string {
    if (!error) return '';

    const rawError = error?.error;
    if (typeof rawError?.detail === 'string' && rawError.detail.trim()) {
      return rawError.detail.trim();
    }
    if (typeof rawError?.description === 'string' && rawError.description.trim()) {
      return rawError.description.trim();
    }
    if (typeof rawError?.message === 'string' && rawError.message.trim()) {
      return rawError.message.trim();
    }
    if (typeof rawError?.['hydra:description'] === 'string' && rawError['hydra:description'].trim()) {
      return rawError['hydra:description'].trim();
    }

    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message.trim();
    }

    const details = error.details;
    if (Array.isArray(details)) {
      const firstViolation = details.find(item => item?.message);
      if (firstViolation?.propertyPath && firstViolation?.message) {
        return `${firstViolation.propertyPath}: ${firstViolation.message}`;
      }
      if (firstViolation?.message) {
        return String(firstViolation.message);
      }
    }

    if (typeof details?.detail === 'string' && details.detail.trim()) {
      return details.detail.trim();
    }
    if (typeof details?.message === 'string' && details.message.trim()) {
      return details.message.trim();
    }

    return '';
  }

  private mapSurveyDetail(item: any): SurveyDetail {
    return {
      ...this.mapSurvey(item),
      status: this.normalizeSurveyStatus(item.status ?? item.state),
      questions: Array.isArray(item.questions)
        ? item.questions.map((question: any) => this.mapQuestion(question))
        : [],
      questionBlocks: Array.isArray(item.questionBlocks)
        ? item.questionBlocks.map((block: any) => this.mapQuestionBlock(block))
        : [],
      assignments: this.mapSurveyAssignments(item.assignments)
    };
  }

  private mapSurveyAssignments(assignments: unknown): NonNullable<SurveyRow['assignments']> {
    return Array.isArray(assignments)
      ? assignments.map((assignment: any) => ({
        id: assignment.id,
        employeeId: assignment.employeeId ?? assignment.employee?.id,
        type: assignment.type || assignment.kind || 'employee',
        name: assignment.name || assignment.title,
        status: assignment.status,
        answeredAt: assignment.answeredAt ?? null,
        dispatchKey: assignment.dispatchKey ?? null,
      }))
      : [];
  }

  private mapEmployeeSurveyAssignmentDetail(item: any): EmployeeSurveyAssignmentDetail {
    return {
      assignmentId: item.assignmentId ?? item.id,
      status: item.status,
      answeredAt: item.answeredAt ?? null,
      survey: {
        id: item.survey?.id,
        title: item.survey?.title,
        status: item.survey?.status,
        companyId: item.survey?.companyId,
        companyName: item.survey?.companyName,
      },
      questions: Array.isArray(item.questions)
        ? item.questions.map((question: any) => {
          const parsedOptions = this.parseEmployeeQuestionOptions(question);
          const parsedType = this.toUiQuestionType(String(question.type || 'text').trim().toLowerCase());
          const resolvedType = this.resolveEmployeeQuestionType(parsedType, parsedOptions);

          return {
            type: resolvedType,
            id: question.id,
            order: question.order,
            block: question.block || '',
            blockDescription: question.blockDescription || '',
            text: question.text || '',
            description: question.description || '',
            options: resolvedType === 'score' ? parsedOptions.slice(0, 2) : parsedOptions,
          };
        })
        : [],
    };
  }

  private mapQuestion(item: any): SurveyQuestion {
    const uiType = this.toUiQuestionType(item.type || item.kind || '');
    const parsedVariables = this.parseQuestionVariables(item.questionVariables);

    return {
      id: item.id,
      block: item.block || item.group || item.section || '',
      text: item.text || item.name || item.question || '',
      description: item.description || item.details || '',
      type: uiType,
      options: uiType === 'score'
        ? this.parseChoiceOptions(item.options).slice(0, 2)
        : this.parseChoiceOptions(item.options),
      questionVariables: this.isVariableAssociationEligibleType(uiType) ? parsedVariables : [],
      priority: item.priority || item.order
    };
  }

  private mapQuestionBlock(item: any): SurveyQuestionGroup {
    return {
      id: item.id,
      name: item.name || '',
      description: item.description || '',
    };
  }

  private normalizeSurveyStatus(status: unknown): string {
    const normalized = String(status ?? 'draft').trim().toLowerCase();
    return normalized === 'published' ? 'published' : 'draft';
  }

  private toApiQuestionType(type?: string): string {
    const normalized = (type || '').trim();
    if (normalized === 'enumerated') {
      return 'choice';
    }

    return normalized || 'text';
  }

  private toApiQuestionOptions(question: SurveyQuestion): any {
    const type = this.toApiQuestionType(question.type);
    const normalized = Array.isArray(question.options)
      ? question.options.map(option => (option || '').trim()).filter(Boolean)
      : [];
    if (!normalized.length) {
      return undefined;
    }

    if (type === 'score') {
      return normalized.slice(0, 2);
    }

    return normalized;
  }

  private toApiQuestionVariables(question: SurveyQuestion): Array<Record<string, any>> {
    if (!Array.isArray(question.questionVariables)) {
      return [];
    }

    return question.questionVariables
      .map(item => {
        const rawMappingCode = (item?.mappingCode || '').trim();
        const mappingCode = this.normalizeQuestionVariableMappingCode(rawMappingCode);
        const mappingParams = this.normalizeQuestionVariableMappingParams(rawMappingCode, this.normalizeMappingParams(item?.mappingParams));
        const isExistsMapping = mappingCode === 'compliance_option_exists';
        return {
          id: item?.id ?? null,
          variableName: (item?.variableName || '').trim(),
          conceptIds: Array.isArray(item?.conceptIds)
            ? item.conceptIds.filter(value => value !== undefined && value !== null && value !== '')
            : [],
          mappingCode,
          mappingParams,
          comparator: isExistsMapping ? 'greater_or_equal' : (item?.comparator || '').trim() || null,
          threshold: isExistsMapping ? '1' : (item?.threshold || '').trim() || null,
          weight: (item?.weight || '').trim() || null,
        };
      })
      .filter(item =>
        item.id !== null
        || item.variableName
        || item.conceptIds.length > 0
        || item.mappingCode
        || (item.mappingParams && Object.keys(item.mappingParams).length > 0)
        || item.comparator
        || item.threshold
        || item.weight
      );
  }

  private normalizeQuestionVariableMappingCode(mappingCode?: string): string | null {
    const normalized = String(mappingCode || '').trim();
    if (!normalized) {
      return null;
    }

    if (normalized === 'compliance_option_exists_yes' || normalized === 'compliance_option_exists_no') {
      return 'compliance_option_exists';
    }

    return normalized;
  }

  private normalizeQuestionVariableMappingParams(
    mappingCode?: string,
    mappingParams?: {[key: string]: string}
  ): {[key: string]: string} | undefined {
    const normalizedCode = String(mappingCode || '').trim();
    if (normalizedCode === 'compliance_option_exists_yes') {
      return {target_value: 'yes'};
    }
    if (normalizedCode === 'compliance_option_exists_no') {
      return {target_value: 'no'};
    }

    return mappingParams;
  }

  private parseChoiceOptions(raw: any): string[] {
    if (Array.isArray(raw)) {
      return raw.map(item => String(item || '').trim()).filter(Boolean);
    }

    if (raw && typeof raw === 'object') {
      const rawMin = raw.min ?? raw.minimum ?? raw.scaleMin ?? raw.rangeMin;
      const rawMax = raw.max ?? raw.maximum ?? raw.scaleMax ?? raw.rangeMax;
      const hasRange = rawMin !== undefined && rawMin !== null && rawMax !== undefined && rawMax !== null;
      if (hasRange) {
        return [String(rawMin).trim(), String(rawMax).trim()].filter(Boolean);
      }

      const choiceRaw = Array.isArray(raw.choiceOptions) ? raw.choiceOptions : [];
      return choiceRaw.map((item: any) => String(item || '').trim()).filter(Boolean);
    }

    return [];
  }

  private parseEmployeeQuestionOptions(question: any): string[] {
    const scoreMin = question?.scoreMin;
    const scoreMax = question?.scoreMax;
    const hasExplicitScoreRange =
      scoreMin !== undefined && scoreMin !== null && scoreMin !== ''
      && scoreMax !== undefined && scoreMax !== null && scoreMax !== '';
    if (hasExplicitScoreRange) {
      return [String(scoreMin).trim(), String(scoreMax).trim()].filter(Boolean);
    }

    return this.parseChoiceOptions(question?.options);
  }

  private resolveEmployeeQuestionType(type: string, options: string[]): string {
    if (type === 'score') {
      return 'score';
    }

    if (type === 'numeric' && this.looksLikeScoreRange(options)) {
      return 'score';
    }

    return type;
  }

  private looksLikeScoreRange(options: string[]): boolean {
    if (!Array.isArray(options) || options.length < 2) {
      return false;
    }

    const min = Number(String(options[0] || '').replace(',', '.'));
    const max = Number(String(options[1] || '').replace(',', '.'));
    return Number.isFinite(min) && Number.isFinite(max) && max > min;
  }

  private parseQuestionVariables(raw: any): SurveyQuestion['questionVariables'] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map(item => ({
        id: item?.id ?? undefined,
        variableId: item?.variableId ?? undefined,
        variableName: item?.variableName ? String(item.variableName) : undefined,
        conceptIds: Array.isArray(item?.conceptIds)
          ? item.conceptIds.filter((value: any) => value !== undefined && value !== null && value !== '')
          : (item?.conceptId !== undefined && item?.conceptId !== null ? [item.conceptId] : undefined),
        conceptNames: Array.isArray(item?.conceptNames)
          ? item.conceptNames.map((value: any) => String(value || '').trim()).filter(Boolean)
          : (item?.conceptName ? [String(item.conceptName)] : undefined),
        mappingCode: item?.mapping ? String(item.mapping) : item?.mappingCode ? String(item.mappingCode) : undefined,
        mappingParams: this.normalizeMappingParams(item?.mappingParams),
        comparator: item?.comparator ? String(item.comparator) as any : undefined,
        threshold: item?.threshold !== undefined && item?.threshold !== null ? String(item.threshold) : undefined,
        weight: item?.weight !== undefined && item?.weight !== null ? String(item.weight) : undefined,
      }))
      .filter(item =>
        item.id !== undefined
        || item.variableId !== undefined
        || item.variableName
        || (item.conceptIds && item.conceptIds.length > 0)
        || item.mappingCode
        || (item.mappingParams && Object.keys(item.mappingParams).length > 0)
        || item.comparator
        || item.threshold
        || item.weight
      );
  }

  private normalizeMappingParams(raw: any): {[key: string]: string} | undefined {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return undefined;
    }

    const normalized: {[key: string]: string} = {};
    Object.entries(raw).forEach(([key, value]) => {
      if (!key?.trim()) {
        return;
      }
      if (value === undefined || value === null) {
        return;
      }
      normalized[key] = String(value);
    });

    return Object.keys(normalized).length ? normalized : undefined;
  }

  private toPersistedQuestionId(questionId: SurveyQuestion['id']): number | undefined {
    if (questionId === undefined || questionId === null || questionId === '') {
      return undefined;
    }

    if (typeof questionId === 'string' && questionId.startsWith('tmp-')) {
      return undefined;
    }

    const parsed = Number(questionId);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private toUiQuestionType(type?: string): string {
    const normalized = (type || '').trim();
    if (normalized === 'enumerated') {
      return 'choice';
    }

    return normalized || 'text';
  }

  private isVariableAssociationEligibleType(type?: string): boolean {
    const normalized = (type || '').trim();
    return normalized === 'boolean'
      || normalized === 'numeric'
      || normalized === 'score'
      || normalized === 'ordinal'
      || normalized === 'choice';
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
